import { useMutation } from "@apollo/client";
import { useCallback, useContext, useEffect, useState } from "react";
import { MUTATION_ACCEPT_PROJECT_INVITE } from "services/graphQL/mutations";
import { ProjectContext } from "context/ProjectProvider";
import { getRiskReportSummary } from "services/auth";
import { QUERY_PROJECTS_PK } from "services/graphQL/queries";
import { useGetGQLClientForProject } from "./init-gql-client";
import { RiskStates } from "../constants";
import { useCIQQuery } from "./ciq-gql-hooks";

export const useProjectPK = (projectId: string, client: any = undefined) => {
  const { data, loading, error, refetch } = useCIQQuery(QUERY_PROJECTS_PK, {
    variables: { projectId },
    client
  });

  return { loading, error, data, refetch };
};

export const useAcceptProjectInvite = () => {
  const { gqlClientForProject, setIdValues, tokenRetrievalState } =
    useGetGQLClientForProject();

  const [acceptProjectInviteMutation] = useMutation<any>(
    MUTATION_ACCEPT_PROJECT_INVITE,
    {
      client: gqlClientForProject
    }
  );

  const [acceptanceState, setAcceptanceState] = useState({
    isLoading: false,
    error: "",
    isCompleted: false
  });

  const accept = useCallback(async () => {
    const acceptResponse = await acceptProjectInviteMutation();
    if (acceptResponse.data.update_project_users.affected_rows === 1) {
      setAcceptanceState({
        isLoading: false,
        error: "",
        isCompleted: true
      });
    } else {
      setAcceptanceState({
        isLoading: false,
        error: "An error occured while accepting the project invite",
        isCompleted: true
      });
    }
  }, [acceptProjectInviteMutation]);

  useEffect(() => {
    if (tokenRetrievalState?.success && gqlClientForProject) {
      accept();
    }

    if (tokenRetrievalState?.error) {
      setAcceptanceState({
        isLoading: false,
        error: tokenRetrievalState.msg,
        isCompleted: true
      });
    }
  }, [accept, gqlClientForProject, tokenRetrievalState]);

  const acceptProjectInvite = (projectId: string) => {
    setAcceptanceState({
      isLoading: true,
      error: "",
      isCompleted: false
    });
    setIdValues({
      projectId
    });
  };

  return { acceptProjectInvite, acceptanceState };
};

export const useProjectFeatureReportSummary = () => {
  const { eventLogs, tokenRetrievalState } = useContext(ProjectContext);

  const [riskSummaryData, setRiskSummaryData] = useState<any>(null);

  const getRiskSummaryData = useCallback(async () => {
    const subSubmittalRiskData = await getRiskReportSummary(
      tokenRetrievalState.token
    );

    if (!subSubmittalRiskData.success || !subSubmittalRiskData.data)
      return null;
    const riskData = subSubmittalRiskData.data?.data;
    const highRiskSubmittal = riskData.find((item: any) => {
      return (
        item.item_type === "Submittals" && item.risk_level === RiskStates.HIGH
      );
    });
    const mediumRiskSubmittal = riskData.find((item: any) => {
      return (
        item.item_type === "Submittals" && item.risk_level === RiskStates.MEDIUM
      );
    });
    const lowRiskSubmittal = riskData.find((item: any) => {
      return (
        item.item_type === "Submittals" && item.risk_level === RiskStates.LOW
      );
    });
    const highRiskMaterial = riskData.find((item: any) => {
      return (
        item.item_type === "Materials" && item.risk_level === RiskStates.HIGH
      );
    });
    const mediumRiskMaterial = riskData.find((item: any) => {
      return (
        item.item_type === "Materials" && item.risk_level === RiskStates.MEDIUM
      );
    });
    const lowRiskMaterial = riskData.find((item: any) => {
      return (
        item.item_type === "Materials" && item.risk_level === RiskStates.LOW
      );
    });

    const risk: any = {
      submittal: {
        high: highRiskSubmittal ? highRiskSubmittal.count : 0,
        medium: mediumRiskSubmittal ? mediumRiskSubmittal.count : 0,
        low: lowRiskSubmittal ? lowRiskSubmittal.count : 0
      },
      material: {
        high: highRiskMaterial ? highRiskMaterial.count : 0,
        medium: mediumRiskMaterial ? mediumRiskMaterial.count : 0,
        low: lowRiskMaterial ? lowRiskMaterial.count : 0
      }
    };
    return risk;
  }, [tokenRetrievalState?.token]);

  useEffect(() => {
    const updateRiskSummaryData = async () => {
      const data = await getRiskSummaryData();
      setRiskSummaryData(data);
    };

    if (eventLogs?.length >= 0) {
      updateRiskSummaryData();
    }
  }, [getRiskSummaryData, eventLogs?.length]);

  return riskSummaryData;
};
